/**
 * createApiWrapper — Next.js Route Handler try/catch 래퍼 생성 팩토리
 *
 * createHttpErrors()로 만든 errorHandler를 주입해 apiWrapper를 생성한다.
 * 모든 Route Handler에서 try/catch를 제거하고 에러 처리를 한 곳으로 모을 수 있다.
 *
 * @example
 * // shared/errors.ts
 * import { createHttpErrors, createApiWrapper } from "@linkup/http";
 * import { ErrorCatalog } from "./errorCatalog";
 *
 * export const { throwHttpError, errorHandler } = createHttpErrors(ErrorCatalog);
 * export const apiWrapper = createApiWrapper(errorHandler);
 *
 * @example
 * // app/api/members/route.ts
 * import { apiWrapper, throwHttpError } from "@/shared/errors";
 *
 * export const GET = apiWrapper(async (req) => {
 *   const user = await queryOne<User>("SELECT ...", []);
 *   if (!user) throwHttpError("NOT_FOUND");
 *   return NextResponse.json({ data: user });
 * });
 *
 * @example
 * // params가 있는 동적 라우트
 * export const DELETE = apiWrapper<{ params: Promise<{ id: string }> }>(
 *   async (req, { params }) => {
 *     const { id } = await params;
 *     await remove("users", { id });
 *     return NextResponse.json({ success: true });
 *   }
 * );
 */

import { NextRequest } from "next/server";

type RouteHandler<TContext = any> = (
    req: NextRequest,
    context: TContext
) => Promise<Response>;

export function createApiWrapper(errorHandler: (err: unknown) => Response) {
    return function apiWrapper<TContext = any>(handler: RouteHandler<TContext>) {
        return async (req: NextRequest, context: TContext): Promise<Response> => {
            try {
                return await handler(req, context);
            } catch (error) {
                return errorHandler(error);
            }
        };
    };
}
